import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Client, ClientType } from '@/types/client'

const API_BASE = import.meta.env.VITE_EMAIL_API_URL || 'https://api.lessablesweb.fr'

interface UpdateClientData {
  id: string
  full_name?: string
  email?: string
  phone?: string | null
  address?: string | null
  type_client?: ClientType
  company?: string | null
  siret?: string | null
  tva_intracommunautaire?: string | null
  adresse_facturation?: string | null
}

export function useUpdateClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateClientData): Promise<Client> => {
      const { id, ...updateData } = data

      // Get current session token for API auth
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Session expirée, veuillez vous reconnecter')

      const response = await fetch(`${API_BASE}/api/clients/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      })

      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.error?.message || 'Erreur lors de la mise a jour')
      }

      return result.data as Client
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['client', data.id] })
      queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
  })
}
